import { EditorContent } from '@tiptap/react';
import * as React from 'react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { serializeMergeFieldTokens, useRichTextEditor } from '@levitate/rich-text';
import { THEME } from '../../../components-v2/Theme/theme';

interface ITextMessageComposerProps {
	tokens: Array<{ symbol: string; text: string }>;
	placeholder?: string;
	autoFocus?: boolean;
	onChange: (text: string) => void;
	onSend: () => void;
	onFilePaste: (files: FileList) => void;
}

export interface ITextMessageComposerHandle {
	insertToken: (symbol: string, text: string) => void;
	removeTokensBySymbols: (symbols: string[]) => void;
	insertText: (text: string) => void;
	reset: () => void;
	focus: () => void;
}

function editorHtmlToPlainText(html: string): string {
	const serialized = serializeMergeFieldTokens(html);
	const doc = new DOMParser().parseFromString(serialized, 'text/html');
	const paragraphs = Array.from(doc.body.children);
	if (paragraphs.length === 0) {
		return doc.body.textContent ?? '';
	}
	return paragraphs.map(p => p.textContent ?? '').join('\n');
}

export const TextMessageComposer = forwardRef<ITextMessageComposerHandle, ITextMessageComposerProps>(
	({ tokens, placeholder, autoFocus, onChange, onSend, onFilePaste }, ref) => {
		const onSendRef = useRef(onSend);
		onSendRef.current = onSend;
		const onFilePasteRef = useRef(onFilePaste);
		onFilePasteRef.current = onFilePaste;
		const onChangeRef = useRef(onChange);
		onChangeRef.current = onChange;

		const [isFocused, setIsFocused] = useState(false);
		const setIsFocusedRef = useRef(setIsFocused);
		setIsFocusedRef.current = setIsFocused;

		const editor = useRichTextEditor({
			initialContent: '',
			starterKitOptions: {
				bold: false,
				italic: false,
				strike: false,
				code: false,
				blockquote: false,
				heading: false,
				horizontalRule: false,
				bulletList: false,
				orderedList: false,
				listItem: false,
				codeBlock: false,
				paragraph: { HTMLAttributes: { style: 'margin:0;padding:0;' } },
			},
			mergeField: { tokens },
			editorProps: {
				handleDOMEvents: {
					focus: () => {
						setIsFocusedRef.current(true);
						return false;
					},
					blur: () => {
						setIsFocusedRef.current(false);
						return false;
					},
				},
				handleKeyDown(view, event) {
					if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
						onSendRef.current();
						return true;
					}
					if (event.key === 'Enter' && !event.shiftKey) {
						view.dispatch(view.state.tr.replaceSelectionWith(view.state.schema.nodes.hardBreak.create()));
						return true;
					}
					return false;
				},
				handlePaste(view, event) {
					const files = event.clipboardData?.files;
					const plain = event.clipboardData?.getData('text/plain') ?? '';
					if (files?.length) {
						onFilePasteRef.current(files);
						if (plain) {
							view.dispatch(view.state.tr.insertText(plain));
						}
						return true;
					}
					if (plain) {
						view.dispatch(view.state.tr.insertText(plain));
						return true;
					}
					return false;
				},
			},
			onUpdate(editorInstance) {
				onChangeRef.current(editorHtmlToPlainText(editorInstance.getHTML()));
			},
		});

		useImperativeHandle(
			ref,
			() => ({
				insertToken(symbol, text) {
					if (!editor) {
						return;
					}
					// Pad the token with spaces so the link it resolves to never glues to adjacent
					// text. Trailing space is added even at the end so typing after the pill stays
					// separated; leading space only when there is non-whitespace content before.
					const { $from, $to } = editor.state.selection;
					const endsWithSpace = (node: typeof $from.nodeBefore) => !!node?.isText && /\s$/.test(node.text ?? '');
					const startsWithSpace = (node: typeof $to.nodeAfter) => !!node?.isText && /^\s/.test(node.text ?? '');
					const chain = editor.chain().focus();
					if ($from.nodeBefore && !endsWithSpace($from.nodeBefore)) {
						chain.insertContent({ type: 'text', text: ' ' });
					}
					chain.insertMergeField({ symbol, text });
					if (!startsWithSpace($to.nodeAfter)) {
						chain.insertContent({ type: 'text', text: ' ' });
					}
					chain.run();
				},
				insertText(text) {
					editor?.chain().focus().insertContent(text).run();
				},
				removeTokensBySymbols(symbols) {
					if (!editor) {
						return;
					}
					const symbolSet = new Set(symbols);
					const { state, dispatch } = editor.view;
					const ranges: { from: number; to: number }[] = [];
					state.doc.descendants((node, pos) => {
						if (node.type.name === 'mergeField' && symbolSet.has(node.attrs.symbol)) {
							ranges.push({ from: pos, to: pos + node.nodeSize });
						}
					});
					if (!ranges.length) {
						return;
					}
					const tr = state.tr;
					for (const { from, to } of [...ranges].reverse()) {
						tr.delete(from, to);
					}
					dispatch(tr);
				},
				reset() {
					editor?.commands.clearContent();
					onChangeRef.current('');
				},
				focus() {
					editor?.commands.focus();
				},
			}),
			[editor]
		);

		useEffect(() => {
			if (autoFocus && editor) {
				editor.commands.focus();
			}
		}, [autoFocus, editor]);

		const containerStyle: React.CSSProperties = {
			position: 'relative',
			width: '100%',
			fontSize: THEME.typography.fontSizes.sm,
			wordWrap: 'break-word',
			overflowY: 'hidden',
		};

		const placeholderStyle: React.CSSProperties = {
			color: THEME.colors.text.light,
			fontSize: THEME.typography.fontSizes.sm,
			left: 0,
			pointerEvents: 'none',
			position: 'absolute',
			top: 0,
		};

		const editorStyle: React.CSSProperties = {
			border: 'none',
			outline: 'none',
			width: '100%',
			minHeight: '1em',
		};

		const isEmpty = editor?.isEmpty ?? true;

		return (
			<div style={containerStyle}>
				{isEmpty && !isFocused && placeholder && <span style={placeholderStyle}>{placeholder}</span>}
				<EditorContent editor={editor} style={editorStyle} />
			</div>
		);
	}
);

TextMessageComposer.displayName = 'TextMessageComposer';
